import { atomWithLocation } from 'jotai-location';
import * as Sentry from '@sentry/nextjs';

interface Location {
  hash: string;
  pathname: string;
  searchParams: URLSearchParams;
}

/**
 * Safe wrapper for requiring next/router that ensures client-side only access
 */
const getRouterSafely = () => {
  // Comprehensive client-side check
  if (
    typeof window === 'undefined' ||
    !globalThis.window ||
    typeof globalThis.window === 'undefined'
  ) {
    return null;
  }

  try {
    return require('next/router').default;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Failed to require next/router:', error);
    }
    return null;
  }
};

/**
 * Check if a parameter is a Next.js route parameter
 */
const isRouteParam = (pathname: string, key: string): boolean =>
  pathname?.includes(`[${key}]`) || pathname?.includes(`[...${key}]`);

/**
 * Parse query parameters from Router.query, filtering out route parameters
 */
const parseRouterQuery = (router: any): URLSearchParams => {
  const searchParams = new URLSearchParams();

  Object.entries(router.query || {}).forEach(([key, value]) => {
    if (isRouteParam(router.pathname, key)) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => searchParams.append(key, v));
    } else if (value) {
      searchParams.set(key, value as string);
    }
  });

  return searchParams;
};

/**
 * Get location from Next.js router if available and ready
 */
const getRouterLocation = (): Location | null => {
  // Only attempt to access router on client side
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    // Double-check we're on client and not in a server-side context
    // This is critical to prevent SSR errors
    if (!globalThis.window || typeof globalThis.window === 'undefined') {
      return null;
    }

    // Safe dynamic import for Next.js router
    const Router = getRouterSafely();
    if (!Router?.isReady) {
      return null;
    }

    return {
      pathname: Router.pathname || window.location.pathname,
      searchParams: parseRouterQuery(Router),
      hash:
        Router.asPath?.split('#')[1] || window.location.hash.replace('#', ''),
    };
  } catch (error) {
    // Log error for debugging but fallback gracefully
    if (
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development'
    ) {
      console.warn('Router access failed in getRouterLocation:', error);
    }
    return null;
  }
};

/**
 * Get location from window.location
 */
const getWindowLocation = (): Location | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return {
      pathname: window.location.pathname,
      searchParams: new URLSearchParams(window.location.search),
      hash: window.location.hash.replace('#', ''),
    };
  } catch (error) {
    // Log error for debugging but fallback gracefully
    if (process.env.NODE_ENV === 'development') {
      console.warn('Window location access failed:', error);
    }
    return null;
  }
};

/**
 * Get default/fallback location
 */
const getDefaultLocation = (): Location => ({
  pathname: '/',
  searchParams: new URLSearchParams(),
  hash: '',
});

/**
 * Get initial location from Next.js router or fallback to window.location
 * This helps prevent hydration mismatches by preloading with correct data
 */
const getInitialLocation = (): Location => {
  // During server-side rendering, return safe defaults
  if (typeof window === 'undefined') {
    return getDefaultLocation();
  }

  // Double-check we're truly on client side
  if (!globalThis.window || typeof globalThis.window === 'undefined') {
    return getDefaultLocation();
  }

  // Try Router first (during hydration, router might have data before isReady)
  try {
    // Safe dynamic import for Next.js router during hydration
    const Router = getRouterSafely();
    if (!Router) {
      return getWindowLocation() || getDefaultLocation();
    }

    // Check if router has useful data even if not ready
    const hasRouterData =
      Router &&
      (Router.isReady ||
        Object.keys(Router.query || {}).length > 0 ||
        (Router.asPath &&
          Router.pathname &&
          Router.asPath !== Router.pathname));

    if (hasRouterData) {
      const searchParams = parseRouterQuery(Router);

      // Also parse query params from asPath during hydration for reliability
      if (Router.asPath) {
        const asPathQuery = Router.asPath.split('?')[1]?.split('#')[0];
        if (asPathQuery) {
          const asPathParams = new URLSearchParams(asPathQuery);
          asPathParams.forEach((value, key) => {
            // Only add if it's not a route param and not already present
            if (
              !(isRouteParam(Router.pathname, key) || searchParams.has(key))
            ) {
              searchParams.set(key, value);
            }
          });
        }
      }

      return {
        pathname: Router.pathname || window.location.pathname,
        searchParams,
        hash:
          Router.asPath?.split('#')[1] || window.location.hash.replace('#', ''),
      };
    }
  } catch (error) {
    // Only report errors on client side to avoid SSR issues
    if (typeof window !== 'undefined' && globalThis.window) {
      // Log for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('Router access failed in getInitialLocation:', error);
      }

      Sentry.captureException(error, {
        tags: {
          component: 'atomWithNextRouter',
          operation: 'getInitialLocation',
          phase: 'router_access',
        },
        extra: {
          hasWindow: typeof window !== 'undefined',
          windowHref: window?.location?.href || 'unknown',
        },
      });
    }
  }

  // Fallback to window.location
  return getWindowLocation() || getDefaultLocation();
};

/**
 * Simple debouncing for URL updates to prevent race conditions
 */
let urlUpdateTimeout: NodeJS.Timeout | null = null;

/**
 * Creates an atomWithLocation configured for Next.js router with proper reactivity
 * This provides consistent Router.events subscription across all location atoms
 *
 * @param initialLocation - Optional server-side location data to prevent hydration mismatches
 */
export const atomWithNextRouter = (initialLocation?: Location) => {
  return atomWithLocation({
    // Preload with server-side data if available, otherwise get client location
    preloaded: initialLocation || getInitialLocation(),
    getLocation: () => {
      if (typeof window === 'undefined') {
        return getDefaultLocation();
      }

      // Try Router first for consistency with applyLocation
      const routerLocation = getRouterLocation();
      if (routerLocation) {
        return routerLocation;
      }

      // Fallback to window.location
      return getWindowLocation() || getDefaultLocation();
    },
    applyLocation: (location: Location) => {
      if (typeof window === 'undefined') {
        return;
      }

      // Double-check we're on client side
      if (!globalThis.window || typeof globalThis.window === 'undefined') {
        return;
      }

      try {
        // Safe dynamic import for Next.js router
        const Router = getRouterSafely();
        if (!Router) {
          return;
        }

        // Use Router.asPath to get the actual resolved path (not the template path)
        // This preserves dynamic route parameters like /help-center/what-is-uppbeat
        const currentPath =
          Router.asPath?.split('?')[0]?.split('#')[0] ||
          window.location.pathname;

        // Build the new URL with resolved path + new query params + hash
        const queryString = location.searchParams?.toString();
        const url = `${currentPath}${queryString ? `?${queryString}` : ''}${location.hash ? `#${location.hash}` : ''}`;

        // Clear any existing timeout to debounce rapid updates
        if (urlUpdateTimeout) {
          clearTimeout(urlUpdateTimeout);
        }

        // Debounce URL updates to prevent race conditions
        urlUpdateTimeout = setTimeout(() => {
          // Use Router.replace with shallow option to maintain Next.js router lifecycle.
          // Shallow prevents data fetching while preserving router events.
          // scroll: false — this atom syncs state to the URL, so it must not
          // reset window.scrollY (Router.replace defaults to scroll:true and
          // would jump the page to top on every filter/search change).
          Router.replace(url, undefined, { shallow: true, scroll: false });
          urlUpdateTimeout = null;
        }, 50); // 50ms debounce
      } catch (error) {
        // Only report errors on client side to avoid SSR issues
        if (typeof window !== 'undefined' && globalThis.window) {
          // Log for debugging in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('Router access failed in applyLocation:', error);
          }

          Sentry.captureException(error, {
            tags: {
              component: 'atomWithNextRouter',
              operation: 'applyLocation',
              phase: 'url_update',
            },
            extra: {
              targetLocation: {
                pathname: location.pathname,
                searchParams: location.searchParams?.toString(),
                hash: location.hash,
              },
              currentHref: window.location.href,
            },
          });
        }
      }
    },
    subscribe: (callback) => {
      // Only subscribe on client side
      if (typeof window === 'undefined') {
        return () => {};
      }

      // Double-check we're on client side
      if (!globalThis.window || typeof globalThis.window === 'undefined') {
        return () => {};
      }

      try {
        // Safe dynamic import for Next.js router
        const Router = getRouterSafely();
        if (!Router) {
          return () => {};
        }

        // Listen to navigation events - no need to avoid feedback since we use history.replaceState
        // Next.js router events handle all navigation types including browser back/forward
        Router.events.on('routeChangeComplete', callback);
        Router.events.on('hashChangeComplete', callback);

        // Cleanup function
        return () => {
          Router.events.off('routeChangeComplete', callback);
          Router.events.off('hashChangeComplete', callback);
        };
      } catch (error) {
        // If Router is not available, log the error and return no-op cleanup
        if (typeof window !== 'undefined' && globalThis.window) {
          // Log for debugging in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('Router access failed in subscribe:', error);
          }

          Sentry.captureException(error, {
            tags: {
              component: 'atomWithNextRouter',
              operation: 'subscribe',
              phase: 'router_subscription',
            },
          });
        }
        return () => {};
      }
    },
  });
};
